import React, { ChangeEvent, CompositionEvent } from 'react';
import { observer } from 'mobx-react';
import { observable, action, computed } from 'mobx';
import intl from 'react-intl-universal';
import ExhibitorStyleSearch from './ExhibitorStyleSearch';
import { ThemeContext } from 'store/ThemeProvider';

export const DEFAULT_IMAGE_SEARCH_RESIZE_WIDTH = 1024;
export const IMAGE_SEARCH_OUTPUT_MIME_TYPE = 'image/jpeg';
export const IMAGE_SEARCH_CANVAS_BACKGROUND = '#ffffff';

const loadImage = (src: string) => {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

const fileToDataUrl = (file: File) => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const resizeImageByCanvas = async (
  file: File,
  targetWidth: number = DEFAULT_IMAGE_SEARCH_RESIZE_WIDTH,
  quality: number = 0.92
) => {
  const dataUrl = await fileToDataUrl(file);
  const image = await loadImage(dataUrl);

  const safeTargetWidth = Math.max(
    1,
    Number(targetWidth) || DEFAULT_IMAGE_SEARCH_RESIZE_WIDTH
  );
  const realWidth = Math.min(
    image.naturalWidth || safeTargetWidth,
    safeTargetWidth
  );
  const ratio = image.naturalWidth ? realWidth / image.naturalWidth : 1;
  const realHeight = Math.max(
    1,
    Math.round((image.naturalHeight || safeTargetWidth) * ratio)
  );

  const canvas = document.createElement('canvas');
  canvas.width = realWidth;
  canvas.height = realHeight;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('canvas context init failed');
  }

  // 向量检索服务对 PNG data URL 的解码不稳定，统一转成 JPEG 再上传。
  // JPEG 不支持透明通道，先铺白底，避免透明 PNG 转换后出现黑底干扰图像特征。
  context.fillStyle = IMAGE_SEARCH_CANVAS_BACKGROUND;
  context.fillRect(0, 0, realWidth, realHeight);
  context.drawImage(image, 0, 0, realWidth, realHeight);

  return canvas.toDataURL(IMAGE_SEARCH_OUTPUT_MIME_TYPE, quality);
};

interface ISearchRow {
  search?: (input: string) => void;
  searchByImage?: (base64: string, file?: File) => void;
  clear?: () => void;
  value?: string;
  onInputChange?: (value: string) => void;
  placeholder?: string;
  imageSearchText?: string;
  showImageSearchLabel?: boolean;
  searchButtonText?: string;
  enableImageSearch?: boolean;
  imageResizeWidth?: number;
  h1Color?: string;
  h2Color?: string;
  mainColor?: string;
  searchLayout?: 'default' | 'button';
}
@observer
class SearchRow extends React.Component<ISearchRow> {
  static contextType = ThemeContext;
  context!: React.ContextType<typeof ThemeContext>;

  @observable input = '';
  @observable isComposing = false;

  textInputRef = React.createRef<HTMLInputElement>();
  albumInputRef = React.createRef<HTMLInputElement>();

  @action changeInput = (e: ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value;
    this.input = nextValue;
    this.props.onInputChange && this.props.onInputChange(nextValue);
  };
  @action handleCompositionStart = () => {
    this.isComposing = true;
  };
  @action handleCompositionEnd = (e: CompositionEvent<HTMLInputElement>) => {
    const nextValue = e.currentTarget.value;
    this.isComposing = false;
    this.input = nextValue;
    this.props.onInputChange && this.props.onInputChange(nextValue);
  };
  @computed get active() {
    return !!this.input;
  }
  @action clear = () => {
    this.input = '';
    this.props.onInputChange && this.props.onInputChange('');
    this.props.clear && this.props.clear();
  };
  @action openImagePicker = () => {
    // 统一走系统原生选择器，避免移动端出现“二次选择”弹层
    this.albumInputRef.current && this.albumInputRef.current.click();
  };
  @action triggerSearch = () => {
    const controlledValue =
      typeof this.props.value === 'string' ? this.props.value : this.input;
    const input = (
      this.textInputRef.current?.value || controlledValue || ''
    ).trim();

    this.input = input;
    this.props.onInputChange && this.props.onInputChange(input);

    if (!input) {
      console.warn('没有输入');
      this.props.clear && this.props.clear();
      return;
    }
    this.props.search && this.props.search(input);
  };
  search = () => {
    window.setTimeout(() => {
      this.triggerSearch();
    }, this.isComposing ? 30 : 0);
  };
  handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) {
      return;
    }

    const base64 = await resizeImageByCanvas(
      file,
      this.props.imageResizeWidth || DEFAULT_IMAGE_SEARCH_RESIZE_WIDTH
    );
    if (base64 && this.props.searchByImage) {
      this.props.searchByImage(base64, file);
    }
  };

  renderCameraIcon() {
    return (
      <svg width="24" height="24" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.75 6.5L11.2667 4.875C11.5669 4.55322 11.9869 4.37012 12.4269 4.37012H13.5731C14.0131 4.37012 14.4331 4.55322 14.7333 4.875L16.25 6.5H19.5C20.6966 6.5 21.6667 7.47005 21.6667 8.66667V18.4167C21.6667 19.6133 20.6966 20.5833 19.5 20.5833H6.5C5.30338 20.5833 4.33334 19.6133 4.33334 18.4167V8.66667C4.33334 7.47005 5.30338 6.5 6.5 6.5H9.75Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="13" cy="13.5417" r="4.33333" stroke="currentColor" strokeWidth="2"/>
      </svg>
    );
  }
  render() {
    const displayValue =
      typeof this.props.value === 'string' ? this.props.value : this.input;
    const imageSearchText = this.props.imageSearchText || '以图搜图';
    const searchButtonText =
      this.props.searchButtonText || intl.get('Search') || '搜索';
    const searchLayout = this.props.searchLayout || 'default';
    const primaryColor =
      this.props.mainColor || this.context?.mainColor || '#50d3cd';
    const titleColor =
      this.props.h1Color || this.context?.h1Color || '#333333';
    const showImageSearchText =
      this.props.enableImageSearch && (searchLayout === 'default' || this.props.showImageSearchLabel);

    const imageTrigger = this.props.enableImageSearch ? (
      <div
        className={[
          'component-ocr-image-trigger',
          showImageSearchText ? '' : 'icon-only',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{ color: primaryColor }}
        onClick={this.openImagePicker}
      >
        {this.renderCameraIcon()}
        {showImageSearchText ? <span>{imageSearchText}</span> : null}
      </div>
    ) : null;

    const actionContent =
      searchLayout === 'button' ? (
        <div className="component-ocr-search-actions">
          {imageTrigger}
          <button
            type="button"
            className="component-ocr-search-button"
            style={{ background: primaryColor }}
            onClick={this.triggerSearch}
          >
            {searchButtonText}
          </button>
        </div>
      ) : (
        imageTrigger
      );

    return (
      <>
        <ExhibitorStyleSearch
          inputRef={this.textInputRef}
          placeholder={
            this.props.placeholder ||
            intl.get('Enter your participate no., letters included')
          }
          value={displayValue}
          onChange={this.changeInput}
          onSearch={this.search}
          onCompositionStart={this.handleCompositionStart}
          onCompositionEnd={this.handleCompositionEnd}          
          searchIconPosition={searchLayout === 'button' ? 'back' : 'front'}
          afterContent={actionContent}
          afterContentClassName={
            searchLayout === 'button' ? 'search-after-actions' : ''
          }
          afterContentWithDivider={searchLayout !== 'button'}
        />
        <input
          ref={this.albumInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={this.handleImageChange}
        />
      </>
    );
  }
}

export default SearchRow;
